import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/api';
import { toast } from 'sonner';
import {
  Bell, BellRing, Clock, AlertTriangle, CheckCircle2, Send,
  RefreshCw, Mail, Loader2, ChevronRight, Calendar, Eye, EyeOff
} from 'lucide-react';

const NotificationsPanel = ({ projectId, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [checkResult, setCheckResult] = useState(null);

  const loadNotifications = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/projects/${projectId}/shop-drawings/notification-log?limit=50`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to load notifications', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open, loadNotifications]);

  const runAutomatedReminders = async () => {
    setChecking(true);
    try {
      const response = await api.post(`/projects/${projectId}/shop-drawings/run-automated-reminders`);
      setCheckResult(response.data.summary);
      toast.success(response.data.message);
      loadNotifications();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to run automated reminders');
    } finally {
      setChecking(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/shop-drawings/notifications/${notificationId}/mark-read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post(`/shop-drawings/notifications/mark-all-read?project_id=${projectId}`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNotificationIcon = (type) => {
    if (type?.includes('overdue')) {
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    }
    if (type?.includes('approaching')) {
      return <Clock className="h-4 w-4 text-amber-500" />;
    }
    if (type?.includes('consultant_response')) {
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    }
    if (type?.includes('status_change')) {
      return <RefreshCw className="h-4 w-4 text-blue-500" />;
    }
    return <Mail className="h-4 w-4 text-slate-500" />;
  };

  const getNotificationBadge = (type) => {
    if (type?.includes('overdue')) {
      return <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">Overdue</Badge>;
    }
    if (type?.includes('approaching')) {
      return <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">Due Soon</Badge>;
    }
    if (type?.includes('consultant_response')) {
      return <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Response</Badge>;
    }
    if (type?.includes('subcontractor')) {
      return <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">Portal</Badge>;
    }
    return <Badge variant="outline" className="text-xs">Notification</Badge>;
  };

  return (
    <>
      {/* Trigger Button */}
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="relative rounded-sm"
        data-testid="notifications-btn"
      >
        {unreadCount > 0 ? (
          <BellRing className="h-4 w-4 mr-2 text-amber-500" />
        ) : (
          <Bell className="h-4 w-4 mr-2" />
        )}
        Reminders
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BellRing className="h-5 w-5" />
              Shop Drawing Reminders & Notifications
            </DialogTitle>
            <DialogDescription>
              Automated reminders for SLA deadlines and status changes
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="notifications" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="notifications">
                Notifications {unreadCount > 0 && `(${unreadCount})`}
              </TabsTrigger>
              <TabsTrigger value="run-check">Run Check</TabsTrigger>
            </TabsList>

            <TabsContent value="notifications" className="space-y-4">
              {/* Actions */}
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadNotifications}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markAllAsRead}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Mark All Read
                  </Button>
                )}
              </div>

              {/* Notifications List */}
              <ScrollArea className="h-[400px] pr-4">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Bell className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No notifications yet</p>
                    <p className="text-sm mt-1">Run a check to generate automated reminders</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-3 rounded-lg border ${
                          notification.read 
                            ? 'bg-slate-50 border-slate-200' 
                            : 'bg-white border-blue-200 shadow-sm'
                        }`}
                        onClick={() => !notification.read && markAsRead(notification.id)}
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5">
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {getNotificationBadge(notification.type)}
                              {!notification.read && (
                                <span className="h-2 w-2 rounded-full bg-blue-500" />
                              )}
                            </div>
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {notification.subject}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              To: {notification.recipient}
                            </p>
                            {notification.metadata?.package_ref && (
                              <p className="text-xs text-slate-600 mt-1">
                                Package: {notification.metadata.package_ref}
                              </p>
                            )}
                            <p className="text-xs text-slate-400 mt-2">
                              {formatDate(notification.created_at)}
                            </p>
                          </div>
                          <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* Email Status Notice */}
              <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span>Email notifications are currently <strong>logged only</strong> (not sent via email)</span>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="run-check" className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Run Automated Reminder Check</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-600">
                    This will check all active shop drawing packages for:
                  </p>
                  <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
                    <li>Contractor review deadlines approaching (within 2 days)</li>
                    <li>Consultant response deadlines approaching</li>
                    <li>Resubmission deadlines for subcontractors</li>
                    <li>Overdue items (past deadline)</li>
                  </ul>

                  <Button
                    onClick={runAutomatedReminders}
                    disabled={checking}
                    className="w-full"
                    data-testid="run-reminders-btn"
                  >
                    {checking ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Run Automated Check
                      </>
                    )}
                  </Button>

                  {checkResult && (
                    <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                      <h4 className="font-medium text-sm">Check Results</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Packages Checked:</span>
                          <span className="font-medium">{checkResult.packages_checked}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Total Reminders:</span>
                          <span className="font-medium">{checkResult.notifications?.length || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-amber-600">Approaching Deadline:</span>
                          <span className="font-medium text-amber-600">
                            {checkResult.approaching_deadline_reminders}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-red-600">Overdue:</span>
                          <span className="font-medium text-red-600">
                            {checkResult.overdue_reminders}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-purple-600">Resubmission:</span>
                          <span className="font-medium text-purple-600">
                            {checkResult.resubmission_reminders}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Scheduling (Coming Soon)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500">
                    In the future, automated reminders will run daily at a scheduled time.
                    For now, use the button above to manually trigger the check.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationsPanel;
